import type { ImplementationVerdict, MetricOutcome } from '@@/api/experiments/implementation-verdict';

/**
 * Turning a verdict into the few words a table cell can hold.
 *
 * The page names a winning variant, so it has to say what it won on: "V-1 won" is not
 * actionable when the win is on revenue and conversions came out flat, and the two goals
 * regularly disagree. Every row therefore carries the goal, the signed lift and the
 * p-value, and the tooltip carries the same for each goal that had numbers.
 *
 * Formatting lives here rather than in the cell so it can be tested without rendering,
 * and so the CSV export and the cell cannot describe one result two different ways.
 */

/** Lift arrives as a percentage, not a fraction, and its sign is the point. */
export const formatLift = (improvement: number | null): string =>
  improvement === null ? '' : `${improvement > 0 ? '+' : ''}${improvement.toFixed(2)}%`;

export const formatPValue = (pValue: number | null): string => {
  if (pValue === null) return '';
  return pValue < 0.0001 ? 'p < 0.0001' : `p = ${pValue.toFixed(4)}`;
};

/** The badge beside the variant name: which goal carried the win, and by how much. */
export const summariseWinner = (outcome: MetricOutcome): string => {
  const lift = formatLift(outcome.improvement);
  return lift ? `${outcome.goal} ${lift}` : outcome.goal;
};

/** One line per goal, for the tooltip. Reads as a sentence, not a row of numbers. */
export const describeOutcome = (outcome: MetricOutcome): string => {
  const lift = formatLift(outcome.improvement);
  const p = formatPValue(outcome.pValue);
  const tail = p ? ` (${p})` : '';

  if (outcome.direction === 'win') {
    return `${outcome.goal}: ${outcome.variantName} is ahead${lift ? ` by ${lift}` : ''}${tail}.`;
  }

  if (outcome.direction === 'loss') {
    return `${outcome.goal}: ${outcome.variantName} is behind${lift ? ` by ${lift}` : ''}${tail}.`;
  }

  if (outcome.direction === 'flat') {
    return `${outcome.goal}: no effect. Sequential testing ruled out the difference this test was sized to find.`;
  }

  return `${outcome.goal}: no difference that chance does not explain${tail}.`;
};

/**
 * Why a row is not in the queue, in the reader's terms.
 *
 * Only reached for rows that are listed for another reason (live now, or ticked by
 * hand); an experiment excluded for one of these is not on the page at all.
 */
export const REASON_LABEL: Record<ImplementationVerdict['reason'], string> = {
  winner: 'A variant beat the control.',
  losing: 'The variant tested came out behind the control.',
  flat: 'The test came out flat.',
  unresolved: 'No result either way.',
  'insufficient-traffic': 'Too little traffic per variant to call a result.',
  'no-data': 'No statistics have been recorded for this experiment.',
};

/** The full tooltip body: the headline, then a line per goal that had numbers. */
export const describeVerdict = (verdict: ImplementationVerdict | null): string[] => {
  if (!verdict) return ['No statistics have been recorded for this experiment.'];

  const currency = verdict.currency && verdict.currency !== 'nill' ? ` Read in ${verdict.currency}.` : '';
  const headline = `${REASON_LABEL[verdict.reason]}${currency}`;

  return [headline, ...verdict.outcomes.map(describeOutcome)];
};
